import { useQuery } from '@apollo/client'
import { useCallback } from 'react'
import { Params, useQueryWithParams } from 'hooks/useQueryWithParams'
import { findAllDeletedTask, findAllDeletedTaskVariables } from '__generatedTypes__/findAllDeletedTask'
import { findAllForCMSTask, findAllForCMSTaskVariables } from '__generatedTypes__/findAllForCMSTask'
import { SearchTypeEnum } from '__generatedTypes__/globalTypes'
import { FIND_ALL_DELETED_TASK, FIND_ALL_FOR_CMS_TASK } from 'queries/customSchedule'
import { ALL_SUBJECT_ID, TasksFilters } from './useTaskFilters'

export const castParams =
  (platformId: string) =>
  ({ page, take, search, order, filters }: Params<TasksFilters>): findAllForCMSTaskVariables => {
    const where: findAllForCMSTaskVariables['query']['where'] = {
      platform: {
        id: platformId,
      },
    }

    if (filters.type) {
      where.type = filters.type.id
    }

    if (filters.subject && filters.subject.id !== ALL_SUBJECT_ID) {
      where.subject = { id: filters.subject.id }
    }

    if (filters.subject && filters.subject.id === ALL_SUBJECT_ID) {
      where.isAllSubjects = true
    }

    return {
      query: {
        skip: (page - 1) * take,
        take,
        search: search ? { query: search, type: SearchTypeEnum.ILike } : undefined,
        order: order?.field ? { [order.field]: order.direction } : undefined,
        where,
      },
    }
  }

export const usePublishedTasks = (platformId: string) => {
  const castParamsCb = useCallback(castParams(platformId), [platformId])

  return useQueryWithParams<
    findAllForCMSTask,
    findAllForCMSTaskVariables,
    'findAllForCMSTask',
    'items',
    'count',
    TasksFilters
  >(FIND_ALL_FOR_CMS_TASK, 'findAllForCMSTask', 'items', 'count', castParamsCb, undefined, {
    fetchPolicy: 'network-only',
  })
}

export const useDeletedTasks = (platformId: string) => {
  const castParamsCb = useCallback(castParams(platformId), [platformId])

  return useQueryWithParams<
    findAllDeletedTask,
    findAllDeletedTaskVariables,
    'findAllDeletedTask',
    'items',
    'count',
    TasksFilters
  >(FIND_ALL_DELETED_TASK, 'findAllDeletedTask', 'items', 'count', castParamsCb, undefined, {
    fetchPolicy: 'network-only',
  })
}

export const useTasksCount = (platformId: string) => {
  const variables = {
    query: {
      where: {
        platform: {
          id: platformId,
        },
      },
    },
  }
  const publishedQuestionsState = useQuery<findAllForCMSTask, findAllForCMSTaskVariables>(FIND_ALL_FOR_CMS_TASK, {
    variables,
  })
  const deletedQuestionsState = useQuery<findAllDeletedTask, findAllDeletedTaskVariables>(FIND_ALL_DELETED_TASK, {
    variables,
  })

  const refetch = useCallback(() => {
    publishedQuestionsState.refetch()
    deletedQuestionsState.refetch()
  }, [])

  return {
    published: publishedQuestionsState.data?.findAllForCMSTask.count ?? 0,
    deleted: deletedQuestionsState.data?.findAllDeletedTask.count ?? 0,
    refetch,
  }
}
